import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customers } from '@/api/customers';
import { dids, type DidRow } from '@/api/dids';
import { featureFlags, type EffectiveFeatureRow } from '@/api/featureFlags';
import { toast } from '@/hooks/use-toast';
import { formatRef } from '@/lib/formatRef';
import { X } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { InviteMethodToggle, type InviteMethod } from '@/components/InviteMethodToggle';
import { PhoneInput } from '@/components/PhoneInput';
import { TimezonePicker } from '@/components/TimezonePicker';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useResellerContext } from '@/lib/reseller-context-store';
import { cn } from '@/lib/cn';

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z.object({
  // Card 1 — Account Details
  resellerId: z.string().optional(),
  name: z.string().min(1, 'Name is required'),
  contactEmail: z.string().email('Must be a valid email').or(z.literal('')).optional(),
  timezone: z.string().optional(),
  language: z.enum(['en', 'he', 'yi']).optional(),

  // Card 2 — Limits. (Feature switches are NOT form fields — they're
  // catalog-driven and held in component state; see featureChoices below.)
  maxChannels: z.number().int().min(1, 'Must be at least 1').optional(),
  maxStorageGb: z.number().int().min(1, 'Must be at least 1').optional(),
  maxRecordings: z.number().int().min(1, 'Must be at least 1').optional(),
  maxIvrNodes: z.number().int().min(1, 'Must be at least 1').optional(),

  // Card 3 — First Owner
  setupUserNow: z.boolean(),
  ownerEmail: z.string().optional(),
  ownerDisplayName: z.string().optional(),
  ownerPhone: z.string().optional(),
}).superRefine((v, ctx) => {
  if (v.setupUserNow) {
    if (!v.ownerEmail || v.ownerEmail.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Owner email is required', path: ['ownerEmail'] });
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.ownerEmail)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be a valid email', path: ['ownerEmail'] });
    }
  }
});

type V = z.infer<typeof schema>;

// ── FieldGroup ────────────────────────────────────────────────────────────────

function FieldGroup({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('space-y-1.5', className)}>{children}</div>;
}

function FieldHint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

function FieldError({ message, id }: { message?: string; id?: string }) {
  if (!message) return null;
  return <p id={id} role="alert" className="text-xs text-destructive">{message}</p>;
}

// ── SwitchRow — label left, toggle right ─────────────────────────────────────

function SwitchRow({
  id, label, hint, checked, onCheckedChange, disabled,
}: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-border/60 last:border-0">
      <div className="min-w-0">
        <label
          htmlFor={id}
          className={cn(
            'text-sm font-medium select-none',
            disabled ? 'text-muted-foreground' : 'cursor-pointer',
          )}
        >
          {label}
        </label>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </div>
  );
}

// ── Feature grouping ─────────────────────────────────────────────────────────

/**
 * Categories whose flags are per-caller behavior / storage plumbing rather
 * than sellable customer modules ('Caller' = bookmark behavior toggles,
 * 'Storage' = backup bucket). They don't belong on the create form — manage
 * them from the customer's detail page (Features card) after creation.
 * Core flags are excluded separately via the catalog's `core` column.
 */
const HIDDEN_CREATE_CATEGORIES = new Set(['Caller', 'Storage']);

function groupFeaturesByCategory(rows: EffectiveFeatureRow[]) {
  const map = new Map<string, EffectiveFeatureRow[]>();
  for (const r of rows) {
    if (!map.has(r.category)) map.set(r.category, []);
    map.get(r.category)!.push(r);
  }
  return Array.from(map.entries())
    .map(([category, list]) => ({
      category,
      rows: [...list].sort((a, b) => a.sort_order - b.sort_order),
    }))
    .sort(
      (a, b) =>
        Math.min(...a.rows.map((r) => r.sort_order)) -
        Math.min(...b.rows.map((r) => r.sort_order)),
    );
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** react-hook-form register options for the numeric limit inputs — empty
 *  string becomes undefined (falls back to backend defaults) instead of NaN. */
const asOptionalNumber = {
  setValueAs: (v: unknown) =>
    v === '' || v === null || v === undefined ? undefined : Number(v),
};

// ── Section divider ───────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-mono font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3 mt-5 first:mt-0">
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function CustomerNew() {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const resellerCtx = useResellerContext((s) => s.current);

  const [method, setMethod] = useState<InviteMethod>('manual_password');
  const [tempPassword, setTempPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Card 3 — numbers to attach to the new account right after creation
  // (Sam 2026-07-02: reseller can add phone numbers during initial setup).
  const [selectedDids, setSelectedDids] = useState<DidRow[]>([]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<V>({
    resolver: zodResolver(schema),
    defaultValues: {
      language: 'en',
      setupUserNow: true,
      maxChannels: 5,
      maxStorageGb: 10,
      maxRecordings: 500,
      maxIvrNodes: 200,
    },
  });

  const setupUserNow = watch('setupUserNow');

  // Resolve reseller ID: SA gets it from reseller context (if entered) or the
  // manual field; reseller_owner gets it injected server-side from their token.
  const resolvedResellerId = me.is_super_admin
    ? (resellerCtx?.id ?? undefined)
    : undefined;

  // ── Feature switches (catalog-driven) ─────────────────────────────────────
  // The card shows the reseller-effective state of every non-core,
  // customer-module feature. Switches start on the reseller's defaults; the
  // backend turns changed values into customer_feature_overrides rows.
  const watchedResellerId = watch('resellerId');
  const manualResellerId =
    me.is_super_admin && !resellerCtx && watchedResellerId && UUID_RE.test(watchedResellerId.trim())
      ? watchedResellerId.trim()
      : undefined;
  // SA scopes the query to the target reseller; reseller_owner's own scope is
  // implicit in their token (the backend ignores reseller_id for them anyway).
  const featureScopeResellerId = me.is_super_admin
    ? (resolvedResellerId ?? manualResellerId)
    : undefined;
  const featuresQueryReady = !me.is_super_admin || Boolean(featureScopeResellerId);

  const effectiveQ = useQuery({
    queryKey: ['feature-flags', 'effective', 'new-customer', featureScopeResellerId ?? 'self'],
    queryFn: () =>
      featureFlags.effective(
        featureScopeResellerId ? { reseller_id: featureScopeResellerId } : {},
      ),
    enabled: featuresQueryReady,
  });

  // User's explicit switch changes, layered over the reseller defaults.
  const [featureChoices, setFeatureChoices] = useState<Record<string, boolean>>({});

  const moduleRows = (effectiveQ.data?.rows ?? []).filter(
    (r) => !r.core && !HIDDEN_CREATE_CATEGORIES.has(r.category),
  );
  const featureGroups = groupFeaturesByCategory(moduleRows);

  /** Payload for customers.create — only rows the reseller tier has ON are
   *  toggleable (the backend rejects enabling a reseller-disabled feature).
   *  Omitted entirely while the list hasn't loaded, so the backend applies
   *  plain reseller defaults. */
  function buildFeaturesPayload(): Record<string, boolean> | undefined {
    if (!effectiveQ.data) return undefined;
    const entries = moduleRows
      .filter((r) => r.enabled)
      .map((r) => [r.key, featureChoices[r.key] ?? true] as const);
    if (entries.length === 0) return undefined;
    return Object.fromEntries(entries);
  }

  return (
    <>
      <PageHeader
        eyebrow="Customers"
        title="New customer"
        description="Create a new customer tenant. A first owner account is optional — you can add users later."
        actions={
          <Button variant="outline" asChild>
            <Link to="/customers">Back</Link>
          </Button>
        }
      />

      {error && (
        <div
          role="alert"
          className="mb-6 max-w-2xl rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      <form
        className="max-w-2xl space-y-5"
        noValidate
        onSubmit={handleSubmit(async (v) => {
          setError(null);

          // Super-admin must have a reseller in context (or entered a reseller
          // context via the reseller drill-down) OR type one manually.
          const resellerId = me.is_super_admin
            ? (resolvedResellerId ?? v.resellerId ?? undefined)
            : undefined;

          if (me.is_super_admin && !resellerId) {
            setError('A reseller ID is required. Enter a reseller from the Resellers page first, or paste a UUID below.');
            return;
          }

          try {
            const r = await customers.create({
              resellerId,
              name: v.name,
              contactEmail: v.contactEmail || null,
              timezone: v.timezone || null,
              // Language selector is hidden until he/yi translations ship —
              // every new account starts as English.
              language: v.language ?? 'en',
              settings: {
                maxChannels: v.maxChannels,
                maxStorageGb: v.maxStorageGb,
                maxRecordings: v.maxRecordings,
                maxIvrNodes: v.maxIvrNodes,
              },
              features: buildFeaturesPayload(),
              firstOwner: v.setupUserNow
                ? {
                    email: v.ownerEmail!,
                    displayName: v.ownerDisplayName || null,
                    phone: v.ownerPhone || null,
                    method,
                    manualPassword: method === 'manual_password' ? tempPassword : undefined,
                  }
                : undefined,
            });

            // Attach the selected phone numbers to the new account. The
            // customer now exists either way, so partial failures must NOT
            // block navigation (re-submitting would create a duplicate
            // account) — report them via toast instead; the reseller can
            // retry from the account's Phone numbers card.
            if (selectedDids.length > 0) {
              const results = await Promise.allSettled(
                selectedDids.map((d) => dids.update(d.id, { customer_id: r.customerId })),
              );
              const failed = selectedDids.filter((_, i) => results[i]!.status === 'rejected');
              if (failed.length > 0) {
                toast({
                  variant: 'destructive',
                  title: 'Some numbers were not attached',
                  description: `${failed.map((d) => d.e164).join(', ')} — attach them from the account's Phone numbers card.`,
                });
              } else {
                toast({
                  title: 'Phone numbers attached',
                  description: `${selectedDids.length} number${selectedDids.length === 1 ? '' : 's'} assigned to the new account.`,
                });
              }
            }
            navigate(`/customers/${r.customerId}`);
          } catch (err) {
            const code = (err as { code?: string }).code;
            if (
              code === 'email_already_exists'
              || code === 'email_already_in_use'
              || code === 'email_taken'
            ) {
              setError('This email is already in use.');
            } else {
              setError(err instanceof Error ? err.message : 'Create failed.');
            }
          }
        })}
      >

        {/* ── Card 1: Account Details ───────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">Account details</CardTitle>
                <CardDescription className="mt-1">
                  Core identity fields for this customer tenant.
                </CardDescription>
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground/60 border border-border/60 rounded px-1.5 py-0.5 shrink-0 mt-0.5">
                01
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Super-admin reseller selector — only shown when not already in reseller context */}
            {me.is_super_admin && !resellerCtx && (
              <FieldGroup>
                <Label htmlFor="resellerId">Reseller ID</Label>
                <Input
                  id="resellerId"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  aria-invalid={!!errors.resellerId}
                  {...register('resellerId')}
                />
                <FieldError message={errors.resellerId?.message} id="resellerId-error" />
                <FieldHint>
                  Paste the target reseller's UUID — or enter a reseller from the Resellers page first to avoid this step.
                </FieldHint>
              </FieldGroup>
            )}

            {/* Reseller context indicator */}
            {me.is_super_admin && resellerCtx && (
              <div className="flex items-center gap-2 rounded-md bg-secondary/60 border border-border/60 px-3 py-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Reseller</span>
                <span className="text-sm font-semibold">{resellerCtx.name}</span>
              </div>
            )}

            {/* Name */}
            <FieldGroup>
              <Label htmlFor="name">
                Account name <span className="text-destructive" aria-hidden>*</span>
              </Label>
              <Input
                id="name"
                placeholder="Acme Corp"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
                {...register('name')}
              />
              <FieldError message={errors.name?.message} id="name-error" />
              <FieldHint>A customer number will be auto-assigned from this reseller's block.</FieldHint>
            </FieldGroup>

            {/* Contact email */}
            <FieldGroup>
              <Label htmlFor="contactEmail">Billing / contact email</Label>
              <Input
                id="contactEmail"
                type="email"
                placeholder="billing@example.com"
                aria-invalid={!!errors.contactEmail}
                aria-describedby={errors.contactEmail ? 'contactEmail-error' : undefined}
                {...register('contactEmail')}
              />
              <FieldError message={errors.contactEmail?.message} id="contactEmail-error" />
              <FieldHint>Used for account notices — not a login credential.</FieldHint>
            </FieldGroup>

            {/* Timezone */}
            <FieldGroup>
              <Label>Timezone</Label>
              <Controller
                control={control}
                name="timezone"
                render={({ field }) => (
                  <TimezonePicker
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  />
                )}
              />
              <FieldHint>Drives schedules, timers, and report timestamps for this account.</FieldHint>
            </FieldGroup>

            {/* HIDDEN until he/yi translations ship — Sam 2026-07-02, bring back then
                (new accounts default to English; see docs/backlog.md — when
                restoring, re-add a SELECT_CLS const with the portal's select
                classes or swap to the shared Select component)
            <FieldGroup>
              <Label htmlFor="language">Language</Label>
              <select id="language" className={SELECT_CLS} {...register('language')}>
                <option value="en">English</option>
                <option value="he">Hebrew</option>
                <option value="yi">Yiddish</option>
              </select>
              <FieldHint>Sets RTL layout for he/yi accounts</FieldHint>
            </FieldGroup>
            */}
          </CardContent>
        </Card>

        {/* ── Card 2: Account Settings ──────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">Account settings</CardTitle>
                <CardDescription className="mt-1">
                  Feature access and resource limits for this tenant.
                </CardDescription>
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground/60 border border-border/60 rounded px-1.5 py-0.5 shrink-0 mt-0.5">
                02
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <SectionLabel>Features</SectionLabel>
            {!featuresQueryReady ? (
              <p className="text-sm text-muted-foreground py-1">
                Enter a reseller (or paste its ID above) to load that reseller's feature defaults.
              </p>
            ) : effectiveQ.isLoading ? (
              <p className="text-sm text-muted-foreground py-1">Loading feature list…</p>
            ) : effectiveQ.isError ? (
              <p role="alert" className="text-sm text-destructive py-1">
                Couldn't load the feature list — the customer will start with the reseller's defaults.
                You can adjust features from the customer's page after creating.
              </p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-3 -mt-1">
                  Pre-set from the reseller's defaults. Any switch you change is saved
                  as a per-customer override.
                </p>
                <div className="space-y-4">
                  {featureGroups.map((g) => (
                    <div key={g.category}>
                      <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground/70 mb-1.5">
                        {g.category}
                      </div>
                      <div className="rounded-md border border-border/60 px-4">
                        {g.rows.map((row) => (
                          <SwitchRow
                            key={row.key}
                            id={`feature-${row.key}`}
                            label={row.display_name}
                            hint={
                              row.enabled
                                ? row.description ?? undefined
                                : 'Turned off for this reseller — enable it on the reseller’s Features page first'
                            }
                            checked={row.enabled ? featureChoices[row.key] ?? true : false}
                            disabled={!row.enabled}
                            onCheckedChange={(next) =>
                              setFeatureChoices((s) => ({ ...s, [row.key]: next }))
                            }
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <SectionLabel>Limits</SectionLabel>
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldGroup>
                <Label htmlFor="maxChannels">Max concurrent channels</Label>
                <Input
                  id="maxChannels"
                  type="number"
                  min={1}
                  aria-invalid={!!errors.maxChannels}
                  {...register('maxChannels', asOptionalNumber)}
                />
                <FieldError message={errors.maxChannels?.message} id="maxChannels-error" />
                <FieldHint>Simultaneous active calls allowed</FieldHint>
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="maxStorageGb">Max storage (GB)</Label>
                <Input
                  id="maxStorageGb"
                  type="number"
                  min={1}
                  aria-invalid={!!errors.maxStorageGb}
                  {...register('maxStorageGb', asOptionalNumber)}
                />
                <FieldError message={errors.maxStorageGb?.message} id="maxStorageGb-error" />
                <FieldHint>Total audio file storage quota</FieldHint>
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="maxRecordings">Max recordings</Label>
                <Input
                  id="maxRecordings"
                  type="number"
                  min={1}
                  aria-invalid={!!errors.maxRecordings}
                  {...register('maxRecordings', asOptionalNumber)}
                />
                <FieldError message={errors.maxRecordings?.message} id="maxRecordings-error" />
                <FieldHint>Total files across all folders</FieldHint>
              </FieldGroup>
              <FieldGroup>
                <Label htmlFor="maxIvrNodes">Max IVR nodes</Label>
                <Input
                  id="maxIvrNodes"
                  type="number"
                  min={1}
                  aria-invalid={!!errors.maxIvrNodes}
                  {...register('maxIvrNodes', asOptionalNumber)}
                />
                <FieldError message={errors.maxIvrNodes?.message} id="maxIvrNodes-error" />
                <FieldHint>Nodes across all IVR trees</FieldHint>
              </FieldGroup>
            </div>
          </CardContent>
        </Card>

        {/* ── Card 3: Phone numbers (optional) ─────────────────────────────── */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">Phone numbers</CardTitle>
                <CardDescription className="mt-1">
                  Optionally attach numbers from your unassigned pool now.
                  Routing is set afterward on the account page (a new account
                  has no IVR trees to route to yet).
                </CardDescription>
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground/60 border border-border/60 rounded px-1.5 py-0.5 shrink-0 mt-0.5">
                03
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <DidSetupPicker
              selected={selectedDids}
              onChange={setSelectedDids}
              isSuperAdmin={me.is_super_admin}
              resellerId={resolvedResellerId ?? null}
            />
          </CardContent>
        </Card>

        {/* ── Card 4: First Owner ───────────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <CardTitle className="text-base">First owner</CardTitle>
                <CardDescription className="mt-1">
                  Optionally create a portal user for this account now.
                </CardDescription>
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground/60 border border-border/60 rounded px-1.5 py-0.5 shrink-0 mt-0.5">
                04
              </div>
            </div>

            {/* Toggle at the top of the card header */}
            <div className="mt-3 flex items-center justify-between rounded-md border border-border/60 bg-secondary/30 px-3.5 py-2.5">
              <div>
                <label
                  htmlFor="setupUserNow"
                  className="text-sm font-medium cursor-pointer select-none"
                >
                  Set up a user account now
                </label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Creates an office manager user linked to this account
                </p>
              </div>
              <Controller
                control={control}
                name="setupUserNow"
                render={({ field }) => (
                  <Switch
                    id="setupUserNow"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </div>
          </CardHeader>

          <CardContent>
            {setupUserNow ? (
              <div className="space-y-4">
                <FieldGroup>
                  <Label htmlFor="ownerEmail">
                    Email <span className="text-destructive" aria-hidden>*</span>
                  </Label>
                  <Input
                    id="ownerEmail"
                    type="email"
                    placeholder="owner@example.com"
                    aria-invalid={!!errors.ownerEmail}
                    aria-describedby={errors.ownerEmail ? 'ownerEmail-error' : undefined}
                    {...register('ownerEmail')}
                  />
                  <FieldError message={errors.ownerEmail?.message} id="ownerEmail-error" />
                </FieldGroup>

                <div className="grid gap-4 sm:grid-cols-2">
                  <FieldGroup>
                    <Label htmlFor="ownerDisplayName">Display name</Label>
                    <Input id="ownerDisplayName" placeholder="Jane Smith" {...register('ownerDisplayName')} />
                  </FieldGroup>
                  <FieldGroup>
                    <Label htmlFor="ownerPhone">Phone</Label>
                    {/* Default prefix scoped to the reseller this customer will
                        belong to (reseller context for SA; reseller_owner
                        resolves from their own JWT server-side). The manual
                        reseller-UUID field is deliberately not wired in — a
                        half-typed UUID would just make noisy lookups. */}
                    <Controller
                      control={control}
                      name="ownerPhone"
                      render={({ field }) => (
                        <PhoneInput
                          id="ownerPhone"
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          resellerId={resolvedResellerId ?? null}
                          placeholder="555 000 0000"
                        />
                      )}
                    />
                  </FieldGroup>
                </div>

                <InviteMethodToggle
                  value={method}
                  onChange={setMethod}
                  tempPassword={tempPassword}
                  onTempPasswordChange={setTempPassword}
                />
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-border bg-secondary/20 px-5 py-6 text-center">
                <p className="text-sm text-muted-foreground">
                  No user will be created. Add users from the customer's{' '}
                  <span className="font-medium text-foreground">Users</span> page after the account is created.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Actions ───────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create customer'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/customers')}>
            Cancel
          </Button>
        </div>
      </form>
    </>
  );
}

// ── DidSetupPicker — attach pool numbers during initial account setup ─────────
//
// Searchable picker (rule 13) over the unassigned pool: for reseller_owner
// RLS scopes the list to their own pool automatically; super_admin sees the
// platform-wide unassigned pool, narrowed to the entered reseller when one
// is in context (assignment derives reseller_id from the customer server-side
// either way). Selected numbers render as removable chips; the actual
// assignment happens after the customer is created (see the submit handler).

function DidSetupPicker({
  selected, onChange, isSuperAdmin, resellerId,
}: {
  selected: DidRow[];
  onChange: (next: DidRow[]) => void;
  isSuperAdmin: boolean;
  resellerId: string | null;
}) {
  const [search, setSearch] = useState('');

  const pool = useQuery({
    queryKey: ['dids', 'setup-pool', resellerId ?? 'all'],
    queryFn: () => dids.list({
      assigned: 'unassigned',
      limit: 200,
      ...(isSuperAdmin && resellerId ? { resellerId } : {}),
    }),
    staleTime: 30_000,
  });

  const selectedIds = new Set(selected.map((d) => d.id));
  const needle = search.replace(/[^\d+]/g, '');
  const candidates = (pool.data?.rows ?? [])
    .filter((d) => !selectedIds.has(d.id))
    .filter((d) => (needle ? d.e164.includes(needle) : true))
    .slice(0, 8);

  return (
    <div className="space-y-3">
      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((d) => (
            <span
              key={d.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-mono text-blue-800"
            >
              {d.e164}
              {d.ref_number != null && (
                <span className="text-[10px] text-blue-500">{formatRef(d.ref_number, 'DID')}</span>
              )}
              <button
                type="button"
                onClick={() => onChange(selected.filter((s) => s.id !== d.id))}
                className="rounded-full p-0.5 hover:bg-blue-100"
                aria-label={`Remove ${d.e164}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search + candidates */}
      <FieldGroup>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search unassigned numbers…"
          aria-label="Search unassigned numbers"
        />
        <FieldHint>
          {pool.isLoading
            ? 'Loading the unassigned pool…'
            : pool.isError
              ? 'Could not load the number pool — numbers can still be attached later from the account page.'
              : `${pool.data?.rows.length ?? 0} unassigned number${(pool.data?.rows.length ?? 0) === 1 ? '' : 's'} available. Leave empty to attach numbers later.`}
        </FieldHint>
      </FieldGroup>
      {candidates.length > 0 && (
        <div className="rounded-md border border-border/60 divide-y divide-border/40">
          {candidates.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => { onChange([...selected, d]); setSearch(''); }}
              className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <span className="font-mono">{d.e164}</span>
              <span className="text-xs text-muted-foreground">
                {d.ref_number != null ? formatRef(d.ref_number, 'DID') : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
